import { Icon, Skeleton, Stack, TableRow, Typography } from '@mui/material';
import {
  AttendanceCode,
  AttendanceSessionEnum,
  GroupType,
  Justification,
  groupTypeMap,
} from '@schooly/api';
import { AttendanceCodeCircleGroup } from '@schooly/components/attendance';
import { useAuth } from '@schooly/components/authentication';
import { AvatarAuth } from '@schooly/components/avatar-auth';
import { AttendanceCodeSelectOption, DropdownAttendanceCodes } from '@schooly/components/filters';
import { JustificationInfo } from '@schooly/components/justifications';
import {
  ArrowDownV2Icon,
  BellIcon,
  GridCell,
  SidebarIcon,
  Spin,
  TruncatedLinks,
  TruncatedText,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { getUserFullName } from '@schooly/utils/user-helpers';
import { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { AbsenceNoticeInfo } from '../../../components/common/AbsenceNoticeInfo/AbsenceNoticeInfo';
import { ContactDetailsTooltip } from '../../../components/common/ContactDetailsTooltip/ContactDetailsTooltip';
import { UpdateEntryCodeParams } from '../../../context/attendance/useAttendanceJustifications';
import { ProfileModalMode } from '../../../context/profile/helpers';
import { getCodesById } from '../attendance-utils';
import { canEditAttendanceEntries } from '../permissions';
import { useJustificationCodeChange } from '../useJustificationCodeChange';

const CODE_CIRCLE_SIZE = 20;
const SESSION_ORDER = [AttendanceSessionEnum.Am, AttendanceSessionEnum.Pm];

type LinkItem = {
  name: string;
  link: { to: string; target: string };
};

export type AttendanceJustificationsTableRowProps = {
  justification: Justification;
  codes: AttendanceCode[];
  schoolId: string;
  updatingIds: Set<string>;
  canViewStudents: boolean;
  codeOptions: AttendanceCodeSelectOption[][];
  onUpdateEntryCode: (params: UpdateEntryCodeParams) => void;
  onOpenSidebar: (justification: Justification) => void;
};

export const AttendanceJustificationsTableRow: FC<AttendanceJustificationsTableRowProps> = ({
  justification,
  codes,
  schoolId,
  canViewStudents,
  updatingIds,
  codeOptions,
  onUpdateEntryCode,
  onOpenSidebar,
}) => {
  const { permissions } = useAuth();
  const { formatMessage } = useIntl();
  const { confirmCodeChange } = useJustificationCodeChange();
  const { relation, attendance_entries, groups } = justification;

  const registerId = attendance_entries[0]?.register.id ?? '';
  const registerDate = attendance_entries[0]?.register.date ?? '';

  const absenceRequest = attendance_entries.find((entry) => entry.absence_request)?.absence_request;
  const absenceRequestComment = absenceRequest?.comment;

  const primaryParent = relation.guardian_relations.find((g) => g.primary);
  const parentHasContactDetails = !!primaryParent?.email || !!primaryParent?.telephone;
  const parentName = primaryParent
    ? getUserFullName({ ...primaryParent, last_name: primaryParent.last_name ?? '' })
    : '';

  const isUpdating = updatingIds.has(justification.id);

  const canEditEntry = canEditAttendanceEntries(permissions);
  const canEditCode = canEditEntry && !isUpdating;

  const { tutorGroups, staff } = useMemo(() => {
    const tutorGroups = groups.filter(
      (currentGroup) => currentGroup.type === groupTypeMap[GroupType.TutorGroup],
    );
    const tutorGroupsLinks = tutorGroups.map((currentGroup) => ({
      name: currentGroup.name,
      link: { to: `/groups/${currentGroup.id}`, target: '_blank' },
    }));
    const staffMap = tutorGroups.reduce<Record<string, LinkItem>>((acc, currentGroup) => {
      for (const currentStaff of currentGroup.staff_preview) {
        if (acc[currentStaff.relation_id]) continue;
        const name = getUserFullName({ ...currentStaff, last_name: currentStaff.last_name ?? '' });
        if (!name) continue;
        acc[currentStaff.relation_id] = {
          name,
          link: { to: `/staff/${currentStaff.relation_id}`, target: '_blank' },
        };
      }
      return acc;
    }, {});

    return { tutorGroups: tutorGroupsLinks, staff: Object.values(staffMap) };
  }, [groups]);

  const sessionEntries = useMemo(
    () =>
      [...attendance_entries]
        .sort((a, b) => SESSION_ORDER.indexOf(a.session) - SESSION_ORDER.indexOf(b.session))
        .map(({ session, attendance_code_id, comments, period_ids }) => ({
          session,
          attendance_code_id,
          comment: comments[0]?.text ?? null,
          period_ids,
        })),
    [attendance_entries],
  );

  const sessions = useMemo(() => sessionEntries.map((entry) => entry.session), [sessionEntries]);

  const sessionsLabel = useMemo(
    () =>
      sessions
        .map((session) => formatMessage({ id: `attendance-${session.toUpperCase()}` }))
        .join('&'),
    [sessions, formatMessage],
  );

  const codesById = useMemo(() => getCodesById(codes), [codes]);

  const selectedCodes = useMemo(() => {
    const codeIds = new Set(sessionEntries.map((entry) => entry.attendance_code_id));

    return [...codeIds].map((codeId) => (codeId ? codesById[codeId] : null));
  }, [sessionEntries, codesById]);

  const hasMultipleCodes = selectedCodes.length > 1;
  const canOpenSidebar = !isUpdating;

  const selectedOption = useMemo(
    () => codeOptions.flat().find((option) => option.id === selectedCodes[0]?.id),
    [codeOptions, selectedCodes],
  );

  const studentName = getUserFullName({ ...relation, last_name: relation.last_name ?? '' });

  const handleSelectCode = useCallback(
    async ({ id }: { id: string }) => {
      const newCode = codesById[id];
      if (!newCode) return;

      const result = await confirmCodeChange({
        newCode,
        codes,
        entries: sessionEntries,
        sessions,
        justification,
        pendingNote: null,
        absenceNotice: absenceRequestComment ?? null,
        relation: { ...relation, last_name: relation.last_name ?? '' },
        date: registerDate,
      });
      if (!result) return;

      onUpdateEntryCode({
        data: {
          relationId: relation.relation_id,
          registerId,
          studentName,
          entries: sessionEntries.map((entry) => ({ ...entry, attendance_code_id: id })),
          justification,
          note: result.note ?? undefined,
        },
      });
    },
    [
      absenceRequestComment,
      codes,
      confirmCodeChange,
      justification,
      onUpdateEntryCode,
      registerDate,
      registerId,
      relation,
      sessionEntries,
      sessions,
      codesById,
      studentName,
    ],
  );

  const renderSelectedCode = (opened: boolean, option?: AttendanceCodeSelectOption) => {
    if (isUpdating) {
      return <Spin sx={(theme) => ({ mr: 0.5, color: theme.palette.primary.main })} />;
    }

    return (
      <>
        {option?.avatar}
        <TypographyWithOverflowHint color="inherit">
          {option?.label ?? formatMessage({ id: 'attendance-NoStatus' })}
        </TypographyWithOverflowHint>
        {!!option?.withNotification && (
          <Icon inverse sx={(theme) => ({ fontSize: theme.spacing(1.5), color: 'text.primary' })}>
            <BellIcon />
          </Icon>
        )}
        {canEditCode && (
          <Icon
            className="arrowIcon"
            sx={{
              width: (theme) => theme.spacing(1),
              transform: opened ? 'rotate(180deg)' : undefined,
            }}
          >
            <ArrowDownV2Icon />
          </Icon>
        )}
      </>
    );
  };

  const renderCode = () => {
    if (!codes.length) {
      return (
        <Typography>
          <Skeleton />
        </Typography>
      );
    }

    if (hasMultipleCodes) {
      return (
        <Stack
          direction="row"
          gap={0.5}
          alignItems="center"
          sx={{ cursor: canOpenSidebar ? 'pointer' : 'default' }}
          onClick={canOpenSidebar ? () => onOpenSidebar(justification) : undefined}
        >
          <AttendanceCodeCircleGroup codes={selectedCodes} size={CODE_CIRCLE_SIZE} />
          <TruncatedText
            items={selectedCodes.map(
              (code) => code?.name ?? formatMessage({ id: 'attendance-NoStatus' }),
            )}
            maxLines={1}
          />
        </Stack>
      );
    }

    return (
      <Stack alignItems="flex-start">
        <DropdownAttendanceCodes
          options={codeOptions}
          selectedOption={selectedOption}
          onSelect={handleSelectCode}
          disabled={!canEditCode}
        >
          {(opened) => (
            <Stack direction="row" gap={1} alignItems="center">
              {renderSelectedCode(opened, selectedOption)}
            </Stack>
          )}
        </DropdownAttendanceCodes>
      </Stack>
    );
  };

  if (!attendance_entries.length) return null;

  return (
    <TableRow
      sx={(theme) => ({
        '& td': {
          '& .MuiTypography-root': {
            color: theme.palette.common.grey2,
          },
          '& .emptyCell': {
            color: theme.palette.common.grey,
          },
          '& .arrowIcon': {
            path: { stroke: theme.palette.common.grey },
          },
          '& .sidebarIcon': {
            color: theme.palette.common.grey,
          },
        },
        '&:hover td': {
          backgroundColor: theme.palette.background.default,
          '& .MuiTypography-root': {
            color: theme.palette.primary.main,
          },
          '& .emptyCell': {
            color: theme.palette.common.grey2,
          },
          '& .arrowIcon': {
            path: { stroke: theme.palette.primary.main },
          },
          '& .sidebarIcon': {
            color: theme.palette.common.grey2,
          },
        },
      })}
    >
      <GridCell py={0.75}>
        <Stack gap={1} direction="row" alignItems="center">
          {studentName ? (
            <Stack direction="row" gap={1} alignItems="center" overflow="hidden">
              <AvatarAuth
                user={{ ...relation, last_name: relation.last_name ?? '' }}
                withAvatarPreview
              />
              {canViewStudents ? (
                <TypographyWithOverflowHint
                  variant="h3"
                  noWrap
                  component={Link}
                  to={{
                    pathname: `/students/${relation.relation_id}`,
                    hash: `#${ProfileModalMode.Attendance}`,
                  }}
                  target="_blank"
                  sx={{ '&:hover': { textDecoration: 'underline' } }}
                >
                  {studentName}
                </TypographyWithOverflowHint>
              ) : (
                <TypographyWithOverflowHint variant="h3" noWrap>
                  {studentName}
                </TypographyWithOverflowHint>
              )}
            </Stack>
          ) : (
            <Typography className="emptyCell">{'-'}</Typography>
          )}
          {!!absenceRequestComment && <AbsenceNoticeInfo comment={absenceRequestComment} />}
          {isUpdating ? (
            <Spin sx={(theme) => ({ color: theme.palette.primary.main })} />
          ) : (
            <JustificationInfo
              justification={justification}
              canViewStudents={canViewStudents}
              relationId={relation.relation_id}
            />
          )}
        </Stack>
      </GridCell>
      <GridCell py={0.75}>
        {tutorGroups.length > 0 ? (
          <TruncatedLinks
            items={tutorGroups}
            tooltipProps={{
              componentsProps: {
                tooltip: { sx: { p: 1.25, width: 200, maxHeight: 400, overflowY: 'scroll' } },
              },
              title: (
                <Stack gap={2} alignItems="flex-start">
                  {tutorGroups.map(({ name, link }) => (
                    <Typography
                      key={link.to}
                      component={Link}
                      to={link.to}
                      target={link.target}
                      sx={(theme) => ({
                        '&:hover': {
                          textDecoration: 'underline',
                          color: theme.palette.primary.main,
                        },
                      })}
                    >
                      {name}
                    </Typography>
                  ))}
                </Stack>
              ),
            }}
          />
        ) : (
          <Typography className="emptyCell">{'-'}</Typography>
        )}
      </GridCell>
      <GridCell py={0.75}>
        {staff.length > 0 ? (
          <TruncatedLinks
            items={staff}
            tooltipProps={{
              componentsProps: {
                tooltip: { sx: { p: 1.25, width: 200, maxHeight: 400, overflowY: 'scroll' } },
              },
              title: (
                <Stack gap={2} alignItems="flex-start">
                  {staff.map(({ name, link }) => (
                    <Typography
                      key={link.to}
                      component={Link}
                      to={link.to}
                      target={link.target}
                      sx={(theme) => ({
                        '&:hover': {
                          textDecoration: 'underline',
                          color: theme.palette.primary.main,
                        },
                      })}
                    >
                      {name}
                    </Typography>
                  ))}
                </Stack>
              ),
            }}
          />
        ) : (
          <Typography className="emptyCell">{'-'}</Typography>
        )}
      </GridCell>
      <GridCell py={0.75}>
        {primaryParent && parentName ? (
          <ContactDetailsTooltip
            schoolId={schoolId}
            relationId={primaryParent.relation_id}
            name={parentHasContactDetails ? parentName : undefined}
            email={primaryParent.email ?? undefined}
            telephone={primaryParent.telephone ?? undefined}
          >
            <Typography
              noWrap
              component={Link}
              to={`/parents/${primaryParent.relation_id}`}
              target="_blank"
              display="block"
              sx={(theme) => ({
                '&:hover': { textDecoration: 'underline', color: theme.palette.primary.main },
              })}
            >
              {parentName}
            </Typography>
          </ContactDetailsTooltip>
        ) : (
          <Typography className="emptyCell">{'-'}</Typography>
        )}
      </GridCell>
      <GridCell py={0.75}>
        {sessionsLabel ? (
          <Typography>{sessionsLabel}</Typography>
        ) : (
          <Typography className="emptyCell">{'-'}</Typography>
        )}
      </GridCell>
      <GridCell py={0.75}>{renderCode()}</GridCell>
      <GridCell
        py={0.75}
        sx={{ cursor: canOpenSidebar ? 'pointer' : 'default' }}
        onClick={canOpenSidebar ? () => onOpenSidebar(justification) : undefined}
      >
        <Icon className="sidebarIcon">
          <SidebarIcon />
        </Icon>
      </GridCell>
    </TableRow>
  );
};
